import Link from "next/link";
import { categoryBrowseHref } from "@/lib/category-data";
import { categoryLabel } from "@/lib/category-taxonomy";
import {
  downloadCountAriaLabel,
  visibleDownloadCount,
} from "@/lib/format-count";
import { fmtDuration } from "@/lib/tls-format";

/** Visible on touch and keyboard alike; the old hover-only tooltip never was. */
export const LONG_TOKEN = "Long: trim first";

export type RowMetaToken = { kind: "duration" | "long" | "installs"; text: string };

export function rowMetaTokens(input: {
  downloads: number;
  duration?: number;
  needsTrimming?: boolean;
  related?: boolean;
}): RowMetaToken[] {
  const { downloads, duration, needsTrimming, related } = input;
  const tokens: RowMetaToken[] = [];
  if (typeof duration === "number" && duration > 0) {
    tokens.push({ kind: "duration", text: fmtDuration(duration) });
  }
  if (needsTrimming) tokens.push({ kind: "long", text: LONG_TOKEN });
  const count = downloads ?? 0;
  if (!related || visibleDownloadCount(count)) {
    tokens.push({
      kind: "installs",
      text: downloadCountAriaLabel(count),
    });
  }
  return tokens;
}

interface RowMetaProps {
  tokens: RowMetaToken[];
  /** DB category name; the row links it to its hub. Omit to leave it out. */
  category?: string | null;
  /** This row's preview is playing: the facts give way to "Playing". */
  isPlaying?: boolean;
  /** Library id for the Chime Studio handoff; without it the trim note is plain text. */
  soundId?: string;
  onCategoryClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

export function RowMeta({ tokens, category, isPlaying = false, soundId, onCategoryClick }: RowMetaProps) {
  const needsTrimming = tokens.some((t) => t.kind === "long");
  const facts = isPlaying ? [{ kind: "playing" as const, text: "Playing" }] : tokens;
  return (
    <div className="sc-meta">
      {category && (
        <Link
          prefetch={false}
          href={categoryBrowseHref(category)}
          onClick={onCategoryClick}
          className="sc-cat tlsr-focus tls-touch-pad"
        >
          {categoryLabel(category)}
        </Link>
      )}
      <span
        className="sc-run"
        data-playing={isPlaying ? "" : undefined}
        data-long={needsTrimming ? "" : undefined}
      >
        {facts.map((token) => (
          <span key={token.kind} className="sc-fact" data-token={token.kind}>
            {token.kind === "long" && soundId ? (
              <Link
                prefetch={false}
                href={`/convert?sound=${encodeURIComponent(soundId)}`}
                className="tlsr-focus tls-touch-pad inline-flex min-h-6 items-center underline underline-offset-2"
                style={{ color: "var(--tls-fg-strong)" }}
              >
                {token.text}
              </Link>
            ) : (
              token.text
            )}
          </span>
        ))}
      </span>
    </div>
  );
}
